/* eslint-disable react/no-array-index-key */
import Button from '@material-ui/core/Button';
import { Alert } from '@orientaction/commons';
import {
  TURNSTILE_TOKEN_FIELD,
  TurnstileWidget,
  useTurnstile,
} from '@orientaction/components/Turnstile';
import { publicIconPath } from '@orientaction/utils';
import { FC, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { UserProfilePhotoContext } from '../../../../Context/UserProfilePhotoContext';
import { apiServiceNative } from '../../../../utils/fetchNative';
import { getSmsApiErrorMessage, getSmsRetryAfterSeconds } from '../../../../utils/smsApiError';
import RedesignImage from '../../shared/components/RedesignImage';
import { useAuthStyles } from '../Auth.styles';
import HelpModal from '../components/HelpModal';
import { OtpInput } from '../components/OtpInput';
import { useVerifyPhoneFormStyles } from './VerifyPhoneForm.styles';

const OTP_LENGTH = 6;

const toPositiveSeconds = (value: unknown): number => {
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 0;
};

const formatResendCountdown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes > 0) {
    return `${minutes}:${remainder.toString().padStart(2, '0')}`;
  }
  return `${remainder}s`;
};

const maskPhone = (phone: string): string => {
  if (!phone || phone.length < 4) return phone || '';
  const visible = phone.slice(-3);
  const prefix = phone.slice(0, 3);
  const masked = 'X'.repeat(Math.max(0, phone.length - 6));
  return `${prefix}${masked}${visible}`;
};

const VerifyPhoneForm: FC = () => {
  const shared = useAuthStyles();
  const local = useVerifyPhoneFormStyles();
  const cls = { ...shared, ...local };
  const history = useHistory();
  const location = useLocation<{ phone?: string; retryAfterSeconds?: number }>();
  const phone = new URLSearchParams(location.search).get('phone') || location.state?.phone || '';

  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [numberSmsSent, setNumberSmsSent] = useState(0);
  const [resendIn, setResendIn] = useState(() => toPositiveSeconds(location.state?.retryAfterSeconds));
  const turnstile = useTurnstile();
  const { resetPhoto, updateUserProfilePhoto } = useContext<any>(UserProfilePhotoContext);

  useEffect(() => {
    if (resendIn <= 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setResendIn(previous => (previous > 0 ? previous - 1 : 0));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  const startResendCooldown = (seconds: number) => {
    const remaining = toPositiveSeconds(seconds);
    if (remaining > 0) {
      setResendIn(remaining);
    }
  };

  const handleOtpChange = (newOtp: string[]) => {
    setOtp(newOtp);
    setError('');
  };

  const handleSubmit = async () => {
    const code = otp.join('');
    if (code.length !== OTP_LENGTH) {
      setError('Veuillez saisir les 6 chiffres du code.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      resetPhoto();
      const result = await apiServiceNative.post('/api/users-permissions/user/verify-sms-code', {
        phone,
        code,
      });
      localStorage.setItem('userInfos', JSON.stringify(result.user));
      localStorage.setItem('userToken', result.jwt);
      updateUserProfilePhoto();
      if (result?.user?.postConfirmationRedirect) {
        history.push(result?.user?.postConfirmationRedirect);
        await apiServiceNative.put(`/api/users/${result?.user?.id}`, {
          postConfirmationRedirect: null,
        });
      } else {
        history.push('/main');
      }
    } catch {
      setError('Code invalide. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0) {
      return;
    }
    if (numberSmsSent >= 3) {
      setError('Vous avez atteint le nombre maximum de tentatives. Veuillez réessayer plus tard.');
      return;
    }
    const turnstileToken = turnstile.getTokenOrError();
    if (!turnstileToken) {
      return;
    }
    setOtp(Array(OTP_LENGTH).fill(''));
    setError('');
    try {
      const result = await apiServiceNative.post('/api/users-permissions/user/send-sms-code', {
        phone,
        [TURNSTILE_TOKEN_FIELD]: turnstileToken,
      });
      startResendCooldown(getSmsRetryAfterSeconds(result));
      setOpenAlert(true);
      setNumberSmsSent(prev => prev + 1);
      turnstile.reset();
    } catch (err) {
      turnstile.reset();
      startResendCooldown(getSmsRetryAfterSeconds(err));
      setError(
        getSmsApiErrorMessage(err, 'Impossible de renvoyer le code. Veuillez réessayer.')
      );
    }
  };

  return (
    <>
      <div className={cls.headingGroup}>
        <h1 className={cls.title}>Vérifiez votre téléphone</h1>
        <div className={cls.subtitle}>
          Pour confirmer votre numéro de téléphone{' '}
          <span className={cls.semibold}>{maskPhone(phone)}</span>, saisissez le code à 6 chiffres
          envoyé par SMS.
        </div>
      </div>

      <div className={cls.otpCard}>
        <OtpInput value={otp} onChange={handleOtpChange} hasError={!!error} />

        {error && <p className={cls.errorTextMd}>{error}</p>}

        <Button
          className={cls.primaryButton}
          onClick={handleSubmit}
          disabled={isSubmitting}
          fullWidth={true}>
          {isSubmitting ? 'Vérification...' : 'Valider'}
        </Button>

        <TurnstileWidget {...turnstile.widgetProps} error={turnstile.error} />

        <button
          type="button"
          className={cls.resendLink}
          onClick={handleResend}
          disabled={resendIn > 0 || numberSmsSent >= 3}>
          {resendIn > 0
            ? `Renvoyer le code (${formatResendCountdown(resendIn)})`
            : 'Renvoyer le code'}
        </button>
      </div>

      <button type="button" className={cls.outlineButton} onClick={() => setHelpOpen(true)}>
        <RedesignImage src={`${publicIconPath}/Info.svg`} alt="" width={16} height={16} />
        <span className={cls.outlineButtonTextPrimary}>Besoin d&apos;aide ?</span>
      </button>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      <Alert
        open={openAlert}
        handleClose={() => setOpenAlert(false)}
        severity="success"
        message="Un nouvel SMS a été envoyé"
        duration={3000}
      />
    </>
  );
};

export default VerifyPhoneForm;
